import { useApi } from "src/api";
import { useLoaders } from "src/components/InlineLoader";
import { Button, Color, Flex, HorizontalDivider, Text } from "src/elements";
import { useModals } from "src/components/Modals";
import { useNavigation } from "src/utils/navigation";
import { pages } from "src/utils";
import { useState } from "react";
import { defaultPspRules } from "src/pages/Merchflows_merchflowId_BasePog_basePogId_/store/types";
import { RoutePostMerchflow_merchflowId_FlowUpdate } from "@CommonApi/merchflow/_merchflowId_/flow-update";
import { PspRules } from "@CommonTypes/backend/PspRules";
import { RulesSection } from "src/components/PlanogramRules/components/RulesSection";
import {
  ProductUpdateAdd,
  ProductUpdateRemove,
  ProductUpdateSwap,
  ProductUpdates,
} from "../store/types";
import { getFacings } from "../store/utils";
import { routeToUrl } from "@CommonUtils/routes";
import { RuleToggle } from "src/components/PlanogramRules/components/RuleToggle";
import { RuleRadios } from "src/components/PlanogramRules/components/RuleRadios";
import { RuleSelect } from "src/components/PlanogramRules/components/RuleSelect";
import { Tooltip } from "src/components";
import styled from "styled-components";

interface Props {
  merchflowId: number;
  productUpdates: ProductUpdates;
}

const TooltipText = styled(Text).attrs({ variant: "small2", color: Color.white })``;

const BALANCE_FACINGS_MODE_OPTIONS = [
  { label: "Rebalance all facings", value: "REBALANCE_ALL_FACINGS" },
  { label: "Affected shelves only", value: "AFFECTED_SHELVES_ONLY" },
  { label: "Empty space only", value: "EMPTY_SPACE_ONLY" },
  { label: "None", value: "NONE" },
] satisfies Array<{
  label: string;
  value: NonNullable<PspRules["range"]["balance_facings_mode"]>;
}>;

const DEFAULT_BALANCE_FACINGS_MODE: NonNullable<PspRules["range"]["balance_facings_mode"]> =
  defaultPspRules.range.balance_facings_mode ?? "EMPTY_SPACE_ONLY";

export const Step4PlanogramRules = ({ merchflowId, productUpdates }: Props) => {
  const { postFlowUpdateApi } = useApi();
  const { hasLoader } = useLoaders();
  const { closeModal } = useModals();
  const { navigate } = useNavigation();

  const [balanceFacingsMode, setBalanceFacingsMode] = useState<
    NonNullable<PspRules["range"]["balance_facings_mode"]>
  >(DEFAULT_BALANCE_FACINGS_MODE);
  const [canApplySqueezeToPlacedProducts, setCanApplySqueezeToPlacedProducts] = useState<
    PspRules["range"]["can_apply_squeeze_to_placed_products"]
  >(defaultPspRules.range.can_apply_squeeze_to_placed_products);

  // Merchandising states
  const [canBreakCasesTraysIfCoreRangeNotFit, setCanBreakCasesTraysIfCoreRangeNotFit] = useState<
    PspRules["merchandising"]["can_break_cases_trays_if_core_range_not_fit"]
  >(defaultPspRules.merchandising.can_break_cases_trays_if_core_range_not_fit);

  const [
    canFillExtraSpaceNextToCasesTraysWithLooseProducts,
    setCanFillExtraSpaceNextToCasesTraysWithLooseProducts,
  ] = useState<
    PspRules["merchandising"]["can_fill_extra_space_next_to_cases_trays_with_loose_products"]
  >(defaultPspRules.merchandising.can_fill_extra_space_next_to_cases_trays_with_loose_products);

  const [
    howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays,
    setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays,
  ] = useState<
    PspRules["merchandising"]["how_should_loose_products_be_placed_with_respect_to_their_cases_trays"]
  >(
    defaultPspRules.merchandising
      .how_should_loose_products_be_placed_with_respect_to_their_cases_trays,
  );

  const [leverageAlternativeOrientations, setLeverageAlternativeOrientations] = useState<
    PspRules["merchandising"]["leverage_alternative_orientations"]
  >(defaultPspRules.merchandising.leverage_alternative_orientations);

  const startUpdate = async () => {
    const response = await postFlowUpdateApi(merchflowId, {
      productsToSwap: productUpdates
        .filter((productUpdate) => productUpdate.type === "swap")
        .map((swap: ProductUpdateSwap) => ({
          pnlDecision: swap.pnlDecision,
          productCodeToAdd:
            swap.add.length === 1 ? swap.add[0].productCode : swap.add.map((p) => p.productCode),
          productCodeToRemove: swap.remove.productCode,
          storeCodes: swap.storeCodes,
          canSqueezeSwapTo: swap.canSqueezeSwapTo,
          swapFacingsMode: swap.swapFacingsMode,
        })),
      productsToInsert: productUpdates
        .filter((productUpdates) => productUpdates.type === "add")
        .map((add: ProductUpdateAdd) => ({
          pnlDecision: add.pnlDecision,
          productCode: add.productToAdd.productCode,
          adjacentProduct:
            add.adjacentProduct && add.adjacentPosition
              ? {
                  adjacentProductCode: add.adjacentProduct.productCode,
                  placeTo: add.adjacentPosition,
                }
              : null,
          facings: getFacings(add.facings),
          reducibleProductCode: add.reducibleProduct?.productCode ?? null,
          fitStrategy: add.fitStrategy,
          storeCodes: add.storeCodes,
          canBeSqueezed: add.canBeSqueezed,
        })),
      productsToRemove: productUpdates
        .filter((productUpdate) => productUpdate.type === "remove")
        .map((remove: ProductUpdateRemove) => ({
          productCode: remove.product.productCode,
          pnlDecision: remove.pnlDecision,
          storeCodes: remove.storeCodes,
        })),
      pspRules: {
        ...defaultPspRules,
        range: {
          ...defaultPspRules.range,
          balance_facings_mode: balanceFacingsMode,
          can_apply_squeeze_to_placed_products: canApplySqueezeToPlacedProducts,
        },
        merchandising: {
          ...defaultPspRules.merchandising,
          can_break_cases_trays_if_core_range_not_fit: canBreakCasesTraysIfCoreRangeNotFit,
          can_fill_extra_space_next_to_cases_trays_with_loose_products:
            canFillExtraSpaceNextToCasesTraysWithLooseProducts,
          how_should_loose_products_be_placed_with_respect_to_their_cases_trays:
            howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays,
          leverage_alternative_orientations: leverageAlternativeOrientations,
        },
      },
    });

    if (response) {
      closeModal("ModalMerchflowReviewUpdate");

      navigate(
        routeToUrl(
          pages.merchflows_merchflowId_StoresAll,
          {
            merchflowId,
          },
          {
            store_pog_trigger_id: response.storePogTriggerId,
          },
        ),
      );
    }
  };

  return (
    <Flex width="100%" column>
      <Flex width="100%" gap="20px" margin="20px 0" flexGrow>
        <RulesSection title="Range" area="range">
          <RuleSelect
            title="How should facings be balanced after applying the product updates?"
            options={BALANCE_FACINGS_MODE_OPTIONS}
            value={balanceFacingsMode}
            setValue={setBalanceFacingsMode}
            tooltip={
              <Tooltip>
                <Flex direction="column">
                  <TooltipText>
                    <b>always:</b> Balance DOS across all shelves, regardless of whether update
                    actions apply to a shelf. This may lead to item facings being changed across all
                    shelves.
                  </TooltipText>
                  <TooltipText>
                    <b>affected shelf:</b> Only balance DOS on shelves where update actions were
                    effected. This restricts changes in item facings to some shelves.
                  </TooltipText>
                  <TooltipText>
                    <b>empty space:</b> Only balance DOS to fill empty spaces on shelves. This
                    restricts changes in item facings to some shelves.
                  </TooltipText>
                  <TooltipText>
                    <b>none:</b> Do not perform any DOS balancing. This minimises changes in item
                    facings.
                  </TooltipText>
                </Flex>
              </Tooltip>
            }
          />

          <HorizontalDivider />

          <RuleToggle
            title="Can we squeeze already placed products to make room for new products?"
            value={canApplySqueezeToPlacedProducts}
            setValue={setCanApplySqueezeToPlacedProducts}
          />
        </RulesSection>

        <RulesSection title="Merchandising" area="merchandising">
          <RuleToggle
            title="Can we break trays if a core range product cannot fit otherwise?"
            value={canBreakCasesTraysIfCoreRangeNotFit}
            setValue={setCanBreakCasesTraysIfCoreRangeNotFit}
          />

          <HorizontalDivider />

          <RuleToggle
            title="Can we fill extra space next to cases / trays with loose products?"
            value={canFillExtraSpaceNextToCasesTraysWithLooseProducts}
            setValue={setCanFillExtraSpaceNextToCasesTraysWithLooseProducts}
          />

          <HorizontalDivider />

          {canFillExtraSpaceNextToCasesTraysWithLooseProducts && (
            <RuleRadios
              title="How should loose products be placed with respect to their cases / trays?"
              value={howShouldLooseProductsBePlacedWithRespectToTheirCasesTrays}
              setValue={setHowShouldLooseProductsBePlacedWithRespectToTheirCasesTrays}
              options={
                [
                  { label: "Behind", value: "BEHIND" },
                  { label: "Front", value: "FRONT" },
                ] satisfies Array<{
                  label: string;
                  value: PspRules["merchandising"]["how_should_loose_products_be_placed_with_respect_to_their_cases_trays"];
                }>
              }
              direction="column"
            />
          )}

          <HorizontalDivider />

          <RuleToggle
            title="Can we leverage alternative orientations to fill empty space on a shelf?"
            value={leverageAlternativeOrientations}
            setValue={setLeverageAlternativeOrientations}
          />
        </RulesSection>
      </Flex>

      <Flex justify="right">
        <Button
          isLoading={hasLoader(RoutePostMerchflow_merchflowId_FlowUpdate)}
          onClick={startUpdate}
        >
          Start Update
        </Button>
      </Flex>
    </Flex>
  );
};
